import { useMemo } from 'react';
import { CRM_OBJECT_TAG, OWNER, PERSONA, TASK_QUEUE, USER } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { RequirablePropertyInputExternalOption } from '../../input/PropertyInputGetters';
import { usePropertyComponentResolver } from '../hooks/usePropertyComponentResolver';
import unescapedText from 'I18n/utils/unescapedText';
import { MARKETING_REASON } from '../../resolvers/CustomResolverReferenceTypes';
import { stringifyReasonId } from '../../resolvers/custom/MarketingReasonReferenceResolver';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { useObjectId } from '../hooks/useObjectId';
import { referenceToOption } from 'reference-resolvers-lite/utils/referenceToOption';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { CONTACT_MARKETABLE_REASON_TYPE } from '../constants/AdditionalPropertyNames';
import { usePropertyValue } from '../hooks/usePropertyValue';

/*
 * The Engagements (i.e. Tasks) `hs_queue_membership_ids` property has
 * `fieldType=checkbox`, meaning it can store multiple values.
 * However, the FE intentionally displays it as a single-select.
 *
 * If we encounter a value that contains multiple ids, only use the first id
 * and ignore the rest.
 *
 * From https://hubspot.slack.com/archives/CPMTBGSAG/p1676646295165459:
 *
 *   The `hs_queue_membership_ids` property was added as a replacement for a
 *   SQL table managing the “Task Queue <--> Task” relationship.
 *   In that migration it felt more flexible for us to allow the property to
 *   contain multiple values (even if the FE didn’t take advantage of that
 *   straight away).
 */
import { jsx as _jsx } from "react/jsx-runtime";
function parseSafeTaskQueueValue(value) {
  if (!value) {
    return value;
  }
  const values = parseMultiEnumValue(value);
  return values.length > 1 ? values[0] : value;
}

// CRM_OBJECT_TAG references are rendered as a colored UITag chosen by the user.
// The external options service returns additionalProperties: { color } of the
// desired color.
const referenceToTagOption = reference => {
  return Object.assign({}, referenceToOption(reference), {
    tagUse: reference.additionalProperties ? reference.additionalProperties.color : 'default'
  });
};
const PropertyInputExternalOptionShim = props => {
  const resolver = usePropertyComponentResolver();
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const value = usePropertyValue();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const propsByReferenceType = useMemo(() => {
    if (!resolver) {
      return {};
    }
    if (resolver.referenceType === USER) {
      return {
        placeholder: unescapedText('customerDataProperties.PropertyInputExternalOption.users.placeholder')
      };
    } else if (resolver.referenceType === OWNER) {
      return {
        placeholder: unescapedText('customerDataProperties.PropertyInputExternalOption.owners.placeholder')
      };
    } else if (resolver.referenceType === PERSONA) {
      return {
        placeholder: unescapedText('customerDataProperties.PropertyInputExternalOption.personas.placeholder')
      };
    } else if (resolver.referenceType === TASK_QUEUE) {
      return {
        placeholder: unescapedText('customerDataProperties.PropertyInputExternalOption.taskQueues.placeholder'),
        // Even though the Task `hs_queue_membership_ids` property definition
        // is a multi-select checkbox, treat it as a single-select on the FE.
        // See https://hubspot.slack.com/archives/CPMTBGSAG/p1676646295165459
        overrideValue: parseSafeTaskQueueValue(value),
        shouldOverrideValue: true,
        multi: false
      };
    } else if (resolver.referenceType === MARKETING_REASON) {
      // Marketing reasons resolve using the reason type+id instead of the value
      const marketableReasonType = getAdditionalPropertyValue(CONTACT_MARKETABLE_REASON_TYPE, objectId, objectTypeId);
      const resolvableValue = marketableReasonType && value ? stringifyReasonId({
        reasonType: marketableReasonType,
        reasonSourceObjectId: value
      }) : value;
      return {
        buttonUse: 'unstyled',
        readOnly: true,
        overrideValue: resolvableValue,
        shouldOverrideValue: true,
        placeholder: EMPTY_PROPERTY_VALUE
      };
    } else if (resolver.referenceType === CRM_OBJECT_TAG) {
      return {
        referenceToOption: referenceToTagOption
      };
    }
    return {};
  }, [resolver, value, objectTypeId, objectId, getAdditionalPropertyValue]);
  return /*#__PURE__*/_jsx(RequirablePropertyInputExternalOption, Object.assign({}, props, propsByReferenceType, {
    resolver: resolver
  }));
};
export default PropertyInputExternalOptionShim;